
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from '@supabase/supabase-js';

// Properly implemented as a React hook function
const useAuthState = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    // Set up the auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (isMounted) {
          console.log('Auth state change:', event, newSession?.user?.email_confirmed_at ? 'verified' : 'unverified');
          
          // Only set session and user if email is confirmed
          if (newSession?.user?.email_confirmed_at) {
            // Set session directly - Supabase auth handles session validity internally
            setSession(newSession);
            setCurrentUser(newSession.user);
          } else {
            // Clear session if user is not verified
            setSession(null);
            setCurrentUser(null);
            
            // If there's an unverified user trying to sign in, sign them out
            if (newSession?.user && !newSession.user.email_confirmed_at) {
              console.log('Unverified user detected, signing out...');
              supabase.auth.signOut();
            }
          }
          
          setLoading(false);
        }
      }
    );
    
    // Check for existing session
    const checkUser = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          if (isMounted) {
            setError(`Session error: ${sessionError.message}`);
            setLoading(false);
          }
          return;
        }
        
        if (isMounted) {
          // Only set session if user is verified
          if (data.session?.user?.email_confirmed_at) {
            setSession(data.session);
            setCurrentUser(data.session.user);
          } else {
            setSession(null);
            setCurrentUser(null);
            
            // Sign out any unverified users
            if (data.session?.user && !data.session.user.email_confirmed_at) {
              console.log('Unverified user session detected, signing out...');
              await supabase.auth.signOut();
            }
          }
          setLoading(false);
        }
      } catch (error: any) {
        if (isMounted) {
          setCurrentUser(null);
          setSession(null);
          setError(`Auth error: ${error.message}`);
          setLoading(false);
        }
      }
    };
    
    // Check user immediately
    checkUser();
    
    // Cleanup listener on unmount
    return () => {
      isMounted = false;
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  return { currentUser, session, loading, error };
};

export default useAuthState;
